import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  BookOpen, Video, FileText, Plus, Trash2, Edit3, Save, X,
  DollarSign, Send, Eye, Upload, Link2, Star, Award, MessageCircle,
  LogOut, Wallet, ArrowDownCircle, ChevronDown, ChevronUp,
  ToggleLeft, ToggleRight, Clock, CheckCircle, XCircle, User
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const getApiUrl = () => {
  const h = window.location.hostname, p = window.location.protocol;
  if (h === 'smartechsystempro.com' || h === 'www.smartechsystempro.com') return `${p}//${h}/api`;
  if (h.includes('emergent.host') || h.includes('emergentagent.com')) return `${p}//${h}/api`;
  return BACKEND_URL ? `${BACKEND_URL}/api` : '/api';
};
const API = getApiUrl();

const apiCall = async (method, url, data = null) => {
  const token = localStorage.getItem("prof_token");
  const config = { method, url: `${API}${url}`, headers: token ? { Authorization: `Bearer ${token}` } : {}, data };
  return axios(config);
};

const ProfessorDashboard = ({ onLogout }) => {
  const [prof, setProf] = useState(null);
  const [activeTab, setActiveTab] = useState("courses");
  const [courses, setCourses] = useState([]);
  const [wallet, setWallet] = useState(null);
  const [messages, setMessages] = useState([]);
  const [certRequests, setCertRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedCourse, setExpandedCourse] = useState(null);

  // Create course form
  const [showCreate, setShowCreate] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [newDesc, setNewDesc] = useState("");
  const [newPrice, setNewPrice] = useState("");
  const [newImage, setNewImage] = useState(null);
  const [newImagePreview, setNewImagePreview] = useState(null);
  const [newBanner, setNewBanner] = useState(null);
  const [newBannerPreview, setNewBannerPreview] = useState(null);
  const [creating, setCreating] = useState(false);

  // Video add
  const [showAddVideo, setShowAddVideo] = useState(null);
  const [videoTitle, setVideoTitle] = useState("");
  const [videoUrl, setVideoUrl] = useState("");
  const [uploading, setUploading] = useState(false);

  // Withdrawal
  const [showWithdraw, setShowWithdraw] = useState(false);
  const [withdrawAmount, setWithdrawAmount] = useState("");
  const [withdrawPhone, setWithdrawPhone] = useState("");

  // Messages
  const [activeConv, setActiveConv] = useState(null);
  const [msgText, setMsgText] = useState("");

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [meRes, coursesRes] = await Promise.all([
        apiCall("get", "/professor/me"),
        apiCall("get", "/professor/courses")
      ]);
      setProf(meRes.data);
      setCourses(coursesRes.data);
    } catch (e) {
      if (e.response?.status === 401) { onLogout(); return; }
    } finally {
      setLoading(false);
    }
  }, [onLogout]);

  const fetchWallet = useCallback(async () => {
    try {
      const res = await apiCall("get", "/professor/wallet");
      setWallet(res.data);
    } catch (e) { if (process.env.NODE_ENV === "development") console.debug("[Prof] wallet:", e?.message); }
  }, []);

  const fetchMessages = useCallback(async () => {
    try {
      const res = await apiCall("get", "/professor/messages");
      setMessages(res.data);
    } catch (e) { if (process.env.NODE_ENV === "development") console.debug("[Prof] messages:", e?.message); }
  }, []);

  const fetchCertRequests = useCallback(async () => {
    try {
      const res = await apiCall("get", "/professor/certificate-requests");
      setCertRequests(res.data);
    } catch (e) { if (process.env.NODE_ENV === "development") console.debug("[Prof] certRequests:", e?.message); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => {
    if (activeTab === "wallet") fetchWallet();
    if (activeTab === "messages") fetchMessages();
    if (activeTab === "certificates") fetchCertRequests();
  }, [activeTab, fetchWallet, fetchMessages, fetchCertRequests]);

  const handleCreateCourse = async (e) => {
    e.preventDefault();
    if (!newTitle.trim() || !newPrice) { toast.error("Titre et prix requis"); return; }
    if (!newImage) { toast.error("Image obligatoire"); return; }
    setCreating(true);
    try {
      const formData = new FormData();
      formData.append("title", newTitle.trim());
      formData.append("description", newDesc);
      formData.append("price", newPrice);
      formData.append("image", newImage);
      if (newBanner) formData.append("banner", newBanner);
      await axios.post(`${API}/professor/courses`, formData, {
        headers: { Authorization: `Bearer ${localStorage.getItem("prof_token")}`, "Content-Type": "multipart/form-data" }
      });
      toast.success("Formation creee!");
      setNewTitle(""); setNewDesc(""); setNewPrice("");
      setNewImage(null); setNewImagePreview(null);
      setNewBanner(null); setNewBannerPreview(null);
      setShowCreate(false);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setCreating(false);
    }
  };

  const handleToggleCourse = async (course) => {
    try {
      await apiCall("put", `/professor/courses/${course.id}`, { is_active: !course.is_active });
      toast.success(course.is_active ? "Formation desactivee" : "Formation activee");
      fetchData();
    } catch { toast.error("Erreur"); }
  };

  const handleDeleteCourse = async (course) => {
    if (!window.confirm(`Supprimer "${course.title}" ?`)) return;
    try {
      await apiCall("delete", `/professor/courses/${course.id}`);
      toast.success("Supprimee");
      fetchData();
    } catch { toast.error("Erreur"); }
  };

  const handleAddVideoUrl = async (courseId) => {
    if (!videoTitle.trim() || !videoUrl.trim()) { toast.error("Titre et URL requis"); return; }
    try {
      await apiCall("post", `/professor/courses/${courseId}/add-video-url`, { title: videoTitle, url: videoUrl });
      toast.success("Video ajoutee");
      setVideoTitle(""); setVideoUrl(""); setShowAddVideo(null);
      fetchData();
    } catch { toast.error("Erreur"); }
  };

  const handleUploadVideo = async (courseId) => {
    const input = document.createElement("input");
    input.type = "file"; input.accept = "video/*";
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      if (file.size > 500 * 1024 * 1024) { toast.error("Max 500MB"); return; }
      setUploading(true);
      const title = videoTitle || file.name;
      try {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("title", title);
        const token = localStorage.getItem("prof_token");
        await axios.post(`${API}/professor/courses/${courseId}/upload-video`, fd, {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 300000,
          maxContentLength: Infinity,
          maxBodyLength: Infinity
        });
        toast.success(`Video "${title}" uploadee!`);
        setVideoTitle("");
        fetchData();
      } catch (err) {
        const detail = err.response?.data?.detail || err.message || "Erreur inconnue";
        toast.error(`Erreur upload: ${detail}`);
        console.error("Upload error:", err);
      } finally {
        setUploading(false);
      }
    };
    input.click();
  };

  const handleUploadDoc = async (courseId) => {
    const input = document.createElement("input");
    input.type = "file"; input.accept = ".pdf,.doc,.docx,.ppt,.pptx";
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      setUploading(true);
      try {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("title", file.name);
        const token = localStorage.getItem("prof_token");
        await axios.post(`${API}/professor/courses/${courseId}/upload-document`, fd, {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 120000,
          maxContentLength: Infinity,
          maxBodyLength: Infinity
        });
        toast.success("Document uploade!"); fetchData();
      } catch (err) {
        toast.error(err.response?.data?.detail || "Erreur upload document");
      } finally {
        setUploading(false);
      }
    };
    input.click();
  };

  const handleWithdraw = async (e) => {
    e.preventDefault();
    try {
      await apiCall("post", "/professor/withdraw", { amount: parseFloat(withdrawAmount), payment_phone: withdrawPhone });
      toast.success("Demande envoyee!");
      setShowWithdraw(false); setWithdrawAmount(""); setWithdrawPhone("");
      fetchWallet();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const handleSendMessage = async () => {
    if (!msgText.trim() || !activeConv) return;
    try {
      await apiCall("post", `/professor/messages/${activeConv.client_id}`, { content: msgText, course_id: activeConv.course_id });
      setMsgText("");
      fetchMessages();
    } catch { toast.error("Erreur"); }
  };

  const handleUploadCertificate = async (clientId, courseId) => {
    const input = document.createElement("input");
    input.type = "file"; input.accept = ".pdf,.jpg,.png";
    input.onchange = async (e) => {
      const file = e.target.files[0]; if (!file) return;
      const fd = new FormData();
      fd.append("file", file);
      fd.append("client_id", clientId);
      fd.append("course_id", courseId);
      try {
        const token = localStorage.getItem("prof_token");
        await axios.post(`${API}/professor/upload-certificate`, fd, {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 60000
        });
        toast.success("Certificat uploade et client notifie!");
        fetchCertRequests();
      } catch (err) {
        toast.error(err.response?.data?.detail || "Erreur upload certificat");
      }
    };
    input.click();
  };

  const handleUploadCertificateForRequest = async (reqId) => {
    const input = document.createElement("input");
    input.type = "file"; input.accept = ".pdf,.jpg,.png";
    input.onchange = async (e) => {
      const file = e.target.files[0]; if (!file) return;
      const fd = new FormData(); fd.append("file", file);
      try {
        const token = localStorage.getItem("prof_token");
        await axios.post(`${API}/professor/certificate-requests/${reqId}/upload`, fd, {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 60000
        });
        toast.success("Certificat uploade et client notifie!");
        fetchCertRequests();
      } catch (err) {
        toast.error(err.response?.data?.detail || "Erreur upload certificat");
      }
    };
    input.click();
  };

  if (loading) return <div className="dashboard-page" style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100vh" }}><div className="spinner"></div></div>;

  return (
    <div className="dashboard-page" data-testid="professor-dashboard">
      {/* Header */}
      <div className="dashboard-header">
        <div className="logo">SMARTECH PRO - <span style={{ color: "#2563EB" }}>Professeur</span></div>
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
          {prof?.photo_url && <img src={prof.photo_url} alt="" style={{ width: 32, height: 32, borderRadius: "50%", objectFit: "cover" }} />}
          <span style={{ fontWeight: 600, fontSize: "0.85rem" }}>{prof?.name}</span>
          <button className="btn btn-sm" style={{ background: "#FEF2F2", color: "#EF4444" }} onClick={onLogout} data-testid="prof-logout"><LogOut size={14} /></button>
        </div>
      </div>

      {/* Stats */}
      <div className="stats-grid" style={{ gridTemplateColumns: "repeat(3, 1fr)" }}>
        <div className="stat-card"><Wallet size={20} className="stat-icon" style={{ color: "#2563EB" }} /><div className="stat-value">{prof?.wallet_balance?.toLocaleString() || 0}</div><div className="stat-label">SOLDE (GDES)</div></div>
        <div className="stat-card"><DollarSign size={20} className="stat-icon" style={{ color: "#16A34A" }} /><div className="stat-value">{prof?.total_earned?.toLocaleString() || 0}</div><div className="stat-label">TOTAL GAGNE</div></div>
        <div className="stat-card"><BookOpen size={20} className="stat-icon" style={{ color: "#F59E0B" }} /><div className="stat-value">{courses.length}</div><div className="stat-label">FORMATIONS</div></div>
      </div>

      {/* Tabs */}
      <div className="tabs-scroll">
        {[
          { id: "courses", label: "Formations", icon: BookOpen },
          { id: "wallet", label: "Portefeuille", icon: Wallet },
          { id: "messages", label: "Messages", icon: MessageCircle },
          { id: "certificates", label: "Certificats", icon: Award },
        ].map(t => (
          <button key={t.id} className={`tab ${activeTab === t.id ? "active" : ""}`} onClick={() => setActiveTab(t.id)} data-testid={`prof-tab-${t.id}`}>
            <t.icon size={14} style={{ marginRight: 4 }} /> {t.label}
            {t.id === "messages" && messages.reduce((a, c) => a + (c.unread || 0), 0) > 0 && (
              <span style={{ marginLeft: 4, background: "#EF4444", color: "white", borderRadius: "50%", width: 18, height: 18, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: "0.6rem" }}>{messages.reduce((a, c) => a + (c.unread || 0), 0)}</span>
            )}
            {t.id === "certificates" && certRequests.filter(r => r.status === "pending").length > 0 && (
              <span style={{ marginLeft: 4, background: "#F59E0B", color: "white", borderRadius: "50%", width: 18, height: 18, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: "0.6rem" }}>{certRequests.filter(r => r.status === "pending").length}</span>
            )}
          </button>
        ))}
      </div>

      <div className="tab-content">
        {/* COURSES TAB */}
        {activeTab === "courses" && (
          <div data-testid="prof-courses-tab">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
              <h3 style={{ margin: 0, fontSize: "1rem", fontWeight: 700 }}>Mes Formations ({courses.length})</h3>
              <button className="btn btn-primary btn-sm" onClick={() => setShowCreate(!showCreate)} data-testid="prof-add-course-btn"><Plus size={14} /> Nouvelle</button>
            </div>

            {showCreate && (
              <div className="card" style={{ marginBottom: "1rem", border: "2px solid #2563EB" }}>
                <h4 style={{ margin: "0 0 0.75rem", fontSize: "0.9rem", color: "#2563EB" }}>Nouvelle formation</h4>
                <form onSubmit={handleCreateCourse}>
                  <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                    <input className="form-input" placeholder="Titre *" value={newTitle} onChange={e => setNewTitle(e.target.value)} data-testid="prof-course-title" required />
                    <textarea className="form-input" rows={2} placeholder="Description" value={newDesc} onChange={e => setNewDesc(e.target.value)} style={{ resize: "vertical" }} />
                    <input className="form-input" type="number" placeholder="Prix (Gdes) *" value={newPrice} onChange={e => setNewPrice(e.target.value)} data-testid="prof-course-price" required />
                    <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap" }}>
                      <label style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "0.4rem 0.75rem", background: "#EFF6FF", color: "#2563EB", borderRadius: 8, fontSize: "0.8rem", fontWeight: 600, cursor: "pointer", border: newImagePreview ? "2px solid #2563EB" : "1px dashed #93C5FD" }} data-testid="prof-course-image-upload">
                        <Upload size={14} /> Image *
                        <input type="file" accept="image/*" style={{ display: "none" }} onChange={e => { const f = e.target.files[0]; if (f) { setNewImage(f); setNewImagePreview(URL.createObjectURL(f)); }}} />
                      </label>
                      {newImagePreview && <img src={newImagePreview} alt="" style={{ width: 48, height: 48, borderRadius: 8, objectFit: "cover" }} />}
                      <label style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "0.4rem 0.75rem", background: "#F0FDF4", color: "#16A34A", borderRadius: 8, fontSize: "0.8rem", fontWeight: 600, cursor: "pointer", border: newBannerPreview ? "2px solid #16A34A" : "1px dashed #BBF7D0" }}>
                        <Upload size={14} /> Banniere
                        <input type="file" accept="image/*" style={{ display: "none" }} onChange={e => { const f = e.target.files[0]; if (f) { setNewBanner(f); setNewBannerPreview(URL.createObjectURL(f)); }}} />
                      </label>
                      {newBannerPreview && <img src={newBannerPreview} alt="" style={{ width: 80, height: 48, borderRadius: 8, objectFit: "cover" }} />}
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: "0.5rem", marginTop: "0.5rem" }}>
                    <button type="submit" className="btn btn-primary btn-sm" disabled={creating} data-testid="prof-create-course-submit"><Save size={14} /> {creating ? "Creation..." : "Creer"}</button>
                    <button type="button" className="btn btn-sm" style={{ background: "#F1F5F9" }} onClick={() => setShowCreate(false)}><X size={14} /> Annuler</button>
                  </div>
                </form>
              </div>
            )}

            {courses.length === 0 ? (
              <div className="empty-state"><BookOpen size={32} /><p>Aucune formation</p></div>
            ) : courses.map(course => (
              <div key={course.id} className="card" style={{ marginBottom: "0.5rem", opacity: course.is_active ? 1 : 0.6 }} data-testid={`prof-course-${course.id}`}>
                <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                  {course.image_url && <img src={course.image_url} alt="" style={{ width: 50, height: 50, borderRadius: 8, objectFit: "cover" }} />}
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, fontSize: "0.9rem" }}>{course.title}</div>
                    <div style={{ display: "flex", gap: "0.5rem", fontSize: "0.7rem", color: "#475569" }}>
                      <span style={{ fontWeight: 700, color: "#2563EB" }}>{course.price?.toLocaleString()} Gdes</span>
                      <span><Video size={11} /> {course.videos?.length || 0}</span>
                      <span><FileText size={11} /> {course.documents?.length || 0}</span>
                      <span style={{ color: "#16A34A" }}>{course.purchases} vendues</span>
                      <span style={{ color: "#16A34A" }}>{course.revenue?.toLocaleString()} Gdes gagne</span>
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: "0.25rem" }}>
                    <button className="btn btn-sm" style={{ background: course.is_active ? "#FEF2F2" : "#F0FDF4", color: course.is_active ? "#EF4444" : "#16A34A", padding: "4px 6px" }} onClick={() => handleToggleCourse(course)}>
                      {course.is_active ? <ToggleRight size={13} /> : <ToggleLeft size={13} />}
                    </button>
                    <button className="btn btn-sm" style={{ background: "#FEF2F2", color: "#EF4444", padding: "4px 6px" }} onClick={() => handleDeleteCourse(course)}><Trash2 size={13} /></button>
                    <button className="btn btn-sm" style={{ background: "#EFF6FF", color: "#2563EB", padding: "4px 6px" }} onClick={() => setExpandedCourse(expandedCourse === course.id ? null : course.id)}>
                      {expandedCourse === course.id ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                    </button>
                  </div>
                </div>
                {expandedCourse === course.id && (
                  <div style={{ marginTop: "0.75rem", borderTop: "1px solid #E2E8F0", paddingTop: "0.5rem" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "0.3rem" }}>
                      <span style={{ fontSize: "0.8rem", fontWeight: 600 }}><Video size={13} /> Videos ({course.videos?.length || 0})</span>
                      <div style={{ display: "flex", gap: "0.25rem" }}>
                        <button className="btn btn-sm" style={{ background: "#EFF6FF", color: "#2563EB", fontSize: "0.7rem" }} onClick={() => { setShowAddVideo(course.id); }} data-testid={`prof-add-video-${course.id}`}><Link2 size={11} /> URL</button>
                        <button className="btn btn-sm" style={{ background: "#EFF6FF", color: "#2563EB", fontSize: "0.7rem" }} onClick={() => { setVideoTitle(""); handleUploadVideo(course.id); }}><Upload size={11} /> Upload</button>
                      </div>
                    </div>
                    {showAddVideo === course.id && (
                      <div style={{ background: "#F8FAFC", borderRadius: 8, padding: "0.5rem", marginBottom: "0.3rem" }}>
                        <input className="form-input" style={{ fontSize: "0.8rem", marginBottom: "0.3rem" }} placeholder="Titre" value={videoTitle} onChange={e => setVideoTitle(e.target.value)} />
                        <input className="form-input" style={{ fontSize: "0.8rem" }} placeholder="URL YouTube/Vimeo" value={videoUrl} onChange={e => setVideoUrl(e.target.value)} />
                        <button className="btn btn-primary btn-sm" style={{ marginTop: "0.3rem", fontSize: "0.7rem" }} onClick={() => handleAddVideoUrl(course.id)}><Save size={11} /> Ajouter</button>
                      </div>
                    )}
                    {(course.videos || []).map((v, i) => (
                      <div key={v.id || i} style={{ padding: "0.25rem 0.5rem", background: "#F8FAFC", borderRadius: 4, marginBottom: "0.2rem", fontSize: "0.75rem", display: "flex", justifyContent: "space-between" }}>
                        <span><Video size={11} /> {v.title}</span><span style={{ color: "#94A3B8" }}>{v.type === "external" ? "URL" : "Upload"}</span>
                      </div>
                    ))}
                    <div style={{ display: "flex", justifyContent: "space-between", marginTop: "0.5rem", marginBottom: "0.3rem" }}>
                      <span style={{ fontSize: "0.8rem", fontWeight: 600 }}><FileText size={13} /> Documents ({course.documents?.length || 0})</span>
                      <button className="btn btn-sm" style={{ background: "#EFF6FF", color: "#2563EB", fontSize: "0.7rem" }} onClick={() => handleUploadDoc(course.id)} disabled={uploading}><Upload size={11} /> Ajouter</button>
                    </div>
                    {(course.documents || []).map((d, i) => (
                      <div key={d.id || i} style={{ padding: "0.25rem 0.5rem", background: "#F8FAFC", borderRadius: 4, marginBottom: "0.2rem", fontSize: "0.75rem" }}><FileText size={11} /> {d.title}</div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* WALLET TAB */}
        {activeTab === "wallet" && wallet && (
          <div data-testid="prof-wallet-tab">
            <div className="card" style={{ textAlign: "center", marginBottom: "1rem", background: "linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)", color: "white" }}>
              <div style={{ fontSize: "0.8rem", opacity: 0.8 }}>Solde disponible</div>
              <div style={{ fontSize: "2rem", fontWeight: 800 }}>{wallet.balance?.toLocaleString()} Gdes</div>
              <div style={{ fontSize: "0.75rem", opacity: 0.7 }}>Total gagne: {wallet.total_earned?.toLocaleString()} Gdes</div>
              <button className="btn btn-sm" style={{ marginTop: "0.5rem", background: "rgba(255,255,255,0.2)", color: "white", border: "1px solid rgba(255,255,255,0.3)" }} onClick={() => setShowWithdraw(!showWithdraw)} data-testid="prof-withdraw-btn">
                <ArrowDownCircle size={14} /> Demander un retrait
              </button>
            </div>

            {showWithdraw && (
              <div className="card" style={{ marginBottom: "1rem", border: "2px solid #2563EB" }}>
                <form onSubmit={handleWithdraw}>
                  <input className="form-input" type="number" placeholder="Montant (Gdes)" value={withdrawAmount} onChange={e => setWithdrawAmount(e.target.value)} style={{ marginBottom: "0.5rem" }} required />
                  <input className="form-input" placeholder="Numero telephone" value={withdrawPhone} onChange={e => setWithdrawPhone(e.target.value)} style={{ marginBottom: "0.5rem" }} required />
                  <button type="submit" className="btn btn-primary btn-sm"><Send size={14} /> Envoyer</button>
                </form>
              </div>
            )}

            {/* Earnings History */}
            <h4 style={{ fontSize: "0.9rem", fontWeight: 700, marginBottom: "0.5rem" }}><DollarSign size={14} /> Historique des gains</h4>
            {(wallet.earnings || []).length === 0 ? <p style={{ fontSize: "0.8rem", color: "#94A3B8" }}>Aucun gain</p> : (
              wallet.earnings.map((e, i) => (
                <div key={e._id || e.id || `${e.course_id || ''}-${i}`} style={{ display: "flex", justifyContent: "space-between", padding: "0.4rem 0.5rem", background: "#F0FDF4", borderRadius: 6, marginBottom: "0.3rem", fontSize: "0.8rem" }}>
                  <span>{e.course_title}</span>
                  <span style={{ fontWeight: 700, color: "#16A34A" }}>+{e.professor_share?.toLocaleString()} Gdes</span>
                </div>
              ))
            )}

            {/* Withdrawal History */}
            <h4 style={{ fontSize: "0.9rem", fontWeight: 700, margin: "1rem 0 0.5rem" }}><Wallet size={14} /> Retraits</h4>
            {(wallet.withdrawals || []).length === 0 ? <p style={{ fontSize: "0.8rem", color: "#94A3B8" }}>Aucun retrait</p> : (
              wallet.withdrawals.map(w => (
                <div key={w.id} style={{ display: "flex", justifyContent: "space-between", padding: "0.4rem 0.5rem", background: w.status === "approved" ? "#F0FDF4" : w.status === "pending" ? "#FFFBEB" : "#FEF2F2", borderRadius: 6, marginBottom: "0.3rem", fontSize: "0.8rem" }}>
                  <span>{w.amount?.toLocaleString()} Gdes - {w.payment_phone}</span>
                  <span style={{ fontWeight: 600, color: w.status === "approved" ? "#16A34A" : w.status === "pending" ? "#D97706" : "#EF4444" }}>
                    {w.status === "approved" ? "Approuve" : w.status === "pending" ? "En attente" : "Rejete"}
                  </span>
                </div>
              ))
            )}
          </div>
        )}

        {/* MESSAGES TAB */}
        {activeTab === "messages" && (
          <div data-testid="prof-messages-tab">
            {activeConv ? (
              <>
                <button className="btn btn-sm" style={{ marginBottom: "0.5rem", background: "#F1F5F9" }} onClick={() => setActiveConv(null)}><X size={14} /> Retour</button>
                <div className="card" style={{ padding: "0.75rem" }}>
                  <h4 style={{ fontSize: "0.85rem", fontWeight: 700, marginBottom: "0.5rem" }}><User size={14} /> {activeConv.client_name}</h4>
                  <div style={{ maxHeight: 300, overflowY: "auto", marginBottom: "0.5rem" }}>
                    {activeConv.messages.map(m => (
                      <div key={m.id} style={{ display: "flex", justifyContent: m.sender === "professor" ? "flex-end" : "flex-start", marginBottom: "0.3rem" }}>
                        <div style={{ maxWidth: "70%", padding: "0.4rem 0.6rem", borderRadius: 12, background: m.sender === "professor" ? "#2563EB" : "#F1F5F9", color: m.sender === "professor" ? "white" : "#1E293B", fontSize: "0.8rem" }}>
                          {m.content}
                          <div style={{ fontSize: "0.6rem", opacity: 0.6, marginTop: 2, textAlign: "right" }}>{m.created_at ? new Date(m.created_at).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" }) : ""}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div style={{ display: "flex", gap: "0.3rem" }}>
                    <input className="form-input" style={{ flex: 1 }} placeholder="Votre message..." value={msgText} onChange={e => setMsgText(e.target.value)} onKeyDown={e => e.key === "Enter" && handleSendMessage()} />
                    <button className="btn btn-primary btn-sm" onClick={handleSendMessage}><Send size={14} /></button>
                  </div>
                </div>
              </>
            ) : (
              <>
                <h3 style={{ fontSize: "1rem", fontWeight: 700, marginBottom: "0.5rem" }}>Conversations</h3>
                {messages.length === 0 ? <div className="empty-state"><MessageCircle size={32} /><p>Aucun message</p></div> : (
                  messages.map((conv, i) => (
                    <div key={conv.client_id || `conv-${i}`} className="card" style={{ marginBottom: "0.3rem", cursor: "pointer", border: conv.unread > 0 ? "2px solid #2563EB" : "1px solid #E2E8F0" }} onClick={() => { setActiveConv(conv); apiCall("put", `/professor/messages/${conv.client_id}/read`); }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <span style={{ fontWeight: 600, fontSize: "0.85rem" }}><User size={14} /> {conv.client_name}</span>
                        {conv.unread > 0 && <span style={{ background: "#2563EB", color: "white", borderRadius: "50%", width: 20, height: 20, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "0.65rem" }}>{conv.unread}</span>}
                      </div>
                      {conv.messages.length > 0 && <div style={{ fontSize: "0.75rem", color: "#64748B", marginTop: 2 }}>{conv.messages[conv.messages.length - 1].content.substring(0, 50)}...</div>}
                    </div>
                  ))
                )}
              </>
            )}
          </div>
        )}

        {/* CERTIFICATES TAB */}
        {activeTab === "certificates" && (
          <div data-testid="prof-certificates-tab">
            <h3 style={{ fontSize: "1rem", fontWeight: 700, marginBottom: "0.75rem" }}>
              <Award size={18} style={{ verticalAlign: "middle", marginRight: 6 }} />
              Certificats - Clients ayant achete
            </h3>
            {certRequests.length === 0 ? (
              <div className="empty-state"><Award size={32} /><p>Aucun client n'a achete de formation</p></div>
            ) : (
              (() => {
                // Group by course
                const byCourse = {};
                certRequests.forEach(r => {
                  if (!byCourse[r.course_id]) byCourse[r.course_id] = { title: r.course_title, clients: [] };
                  byCourse[r.course_id].clients.push(r);
                });
                return Object.entries(byCourse).map(([courseId, data]) => (
                  <div key={courseId} className="card" style={{ marginBottom: "0.75rem" }}>
                    <h4 style={{ fontSize: "0.9rem", fontWeight: 700, color: "#1E293B", marginBottom: "0.5rem", borderBottom: "1px solid #E2E8F0", paddingBottom: "0.3rem" }}>
                      <BookOpen size={14} style={{ verticalAlign: "middle", marginRight: 4 }} />
                      {data.title}
                      <span style={{ fontSize: "0.7rem", color: "#64748B", fontWeight: 400, marginLeft: 8 }}>{data.clients.length} client(s)</span>
                    </h4>
                    {data.clients.map((client, idx) => (
                      <div key={`${client.client_id}-${idx}`} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0.5rem", background: idx % 2 === 0 ? "#F8FAFC" : "white", borderRadius: 6, marginBottom: "0.25rem" }}>
                        <div>
                          <div style={{ fontWeight: 600, fontSize: "0.85rem", color: "#1E293B" }}>
                            <User size={13} style={{ verticalAlign: "middle", marginRight: 4 }} />
                            {client.client_name}
                          </div>
                          {client.client_phone && <div style={{ fontSize: "0.7rem", color: "#94A3B8" }}>{client.client_phone}</div>}
                          {client.purchase_date && <div style={{ fontSize: "0.65rem", color: "#94A3B8" }}>Achete le {new Date(client.purchase_date).toLocaleDateString("fr-FR")}</div>}
                        </div>
                        <div>
                          {client.has_certificate ? (
                            <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: "0.75rem", color: "#16A34A", fontWeight: 600, background: "#F0FDF4", padding: "0.25rem 0.5rem", borderRadius: 6 }}>
                              <CheckCircle size={13} /> Certificat envoye
                            </span>
                          ) : client.has_pending_request ? (
                            <button className="btn btn-primary btn-sm" style={{ fontSize: "0.75rem" }} onClick={() => handleUploadCertificateForRequest(client.request_id)} data-testid={`upload-cert-req-${client.request_id}`}>
                              <Upload size={13} /> Envoyer (demande recue)
                            </button>
                          ) : (
                            <button className="btn btn-sm" style={{ fontSize: "0.75rem", background: "#EFF6FF", color: "#2563EB", border: "1px solid #93C5FD" }} onClick={() => handleUploadCertificate(client.client_id, client.course_id)} data-testid={`upload-cert-${client.client_id}-${client.course_id}`}>
                              <Upload size={13} /> Envoyer certificat
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ));
              })()
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProfessorDashboard;
